import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import b from 'b_';
import * as d3 from 'd3';
import { ChartStore } from '../../stores/DashboardStore';
import { formatInteger } from '../../format';
import RangePickerModal from '../RangePickerModal/RangePickerModal';
import Button, { Loader } from '../Button';
import './WidgetLineChart.css';

const block = b.lock('WidgetLineChart');

class WidgetLineChart extends Component {
    static propTypes = {
        store: ChartStore.PropType.isRequired,
        title: PropTypes.node.isRequired,
        total1: PropTypes.node,
        total2: PropTypes.node,
        legend1: PropTypes.node,
        legend2: PropTypes.node,
    };

    svgContainerEl = null;

    svgContainerRel = (el) => { this.svgContainerEl = el; };

    svgEl = null;

    svgRel = (el) => {
        this.svgEl = el;
        setTimeout(this.drawChart, 150);
    };

    prevWidth = 0;
    prevHeight = 0;

    resizeInt = null;

    componentDidMount() {
        this.drawChart();

        this.resizeInt = setInterval(() => {
            if (!this.svgContainerEl || !this.svgEl) {
                this.prevWidth = 0;
                this.prevHeight = 0;
                return;
            }

            if (this.svgEl.firstElementChild !== null
                && this.prevWidth > 0
                && this.prevHeight > 0
                && this.prevWidth === this.svgContainerEl.clientWidth
                && this.prevHeight === this.svgContainerEl.clientHeight) {
                return;
            }

            this.drawChart();
        }, 150);

        reaction(() => this.props.store.points, this.drawChart);
    }

    componentWillUnmount() {
        clearInterval(this.resizeInt);
    }

    drawChart = () => {
        if (!this.svgContainerEl || !this.svgEl) {
            return;
        }

        const points = this.props.store.points;
        const allPoints = [...points[0], ...points[1]];

        const fullWidth = this.svgContainerEl.clientWidth;
        const fullHeight = this.svgContainerEl.clientHeight;
        this.prevWidth = fullWidth;
        this.prevHeight = fullHeight;

        // IE11 does not support innerHTML on SVG
        while (this.svgEl.firstChild) {
            this.svgEl.removeChild(this.svgEl.firstChild);
        }

        const graph = d3.select(this.svgEl);
        graph.attr('width', this.prevWidth);
        graph.attr('height', this.prevHeight);

        const margin = { left: 50, right: 10, top: 10, bottom: 40 };
        const graphWidth = fullWidth - margin.left - margin.right;
        const graphHeight = fullHeight - margin.top - margin.bottom;

        // X-AXIS
        const xScale = d3.scaleLinear()
            .domain(d3.extent(allPoints, (d) => d.x))
            .range([0, graphWidth]);

        graph.append('g')
            .attr('transform', `translate(${margin.left} ${graphHeight + margin.top})`)
            .call((g) => {
                g.call(d3.axisBottom(xScale)
                    .tickSize(0)
                    .ticks(Math.min(points[0].length, Math.floor(graphWidth / 80)))
                    .tickFormat(this.props.store.range.formatX));
                g.selectAll('.domain').remove();
                g.selectAll('.tick text')
                    .attr('dy', '30')
                    .style('font-size', '13px')
                    .style('color', 'rgba(0,0,0,.87)');
            });

        // Y-AXIS
        const maxY = Math.max(d3.max(allPoints, (d) => d.y), 1);
        const yScale = d3.scaleLinear()
            .domain([0, Math.floor(1.05 * maxY)])
            .range([graphHeight, 0]);

        graph.append('g')
            .attr('transform', `translate(${margin.left + graphWidth} ${margin.top})`)
            .call((g) => {
                g.call(d3.axisLeft(yScale)
                    .tickSize(graphWidth)
                    .ticks(Math.min(Math.floor(graphHeight / 30), maxY))
                    .tickFormat(d3.format('.2s')));
                g.selectAll('.domain').remove();
                g.selectAll('.tick line')
                    .attr('stroke', '#E0E0E0')
                    .attr('stroke-width', '1px');
                g.selectAll('.tick text')
                    .attr('dx', '-10')
                    .attr('dy', '0')
                    .style('font-size', '13px')
                    .style('color', 'rgba(0,0,0,.87)');
            });

        // plot
        [...points].reverse().forEach((data, index) => {
            graph.append('path')
                .data([data])
                .attr('class', block('GraphArea', { index }))
                .attr('transform', `translate(${margin.left} ${margin.top})`)
                .attr('d', d3.area()
                    .x((d) => xScale(d.x))
                    .y0(graphHeight)
                    .y1((d) => yScale(d.y)));
        });
    };

    renderBody() {
        const { store } = this.props;
        const single = !this.props.legend1 || !this.props.legend2;
        return <div className={block('Content')}>
            <div className={block('Totals')}>
                <div className={block('Title')}>
                    {this.props.title}
                </div>
                {[this.props.total1, this.props.total2].map((legend, index) => (legend ? <Fragment
                    key={legend}
                >
                    <div className={block('TotalValue', { index })}>
                        {formatInteger(store.totals[index] || 0)}
                    </div>
                    <div className={block('TotalLabel')}>
                        {legend}
                    </div>
                    <div className={block('TotalRange')}>
                        ({store.range.title})
                    </div>
                </Fragment> : null))}
            </div>
            <div className={block('Chart', { single })}>
                <div className={block('LegendContainer')}>
                    {store.range.isCustom ? <div className={block('RangeWrapper')}>
                        <div className={block('RangeLabel')}>Date Range</div>
                        <Button
                            className={block('RangeButton')}
                            icon="date_range"
                            onClick={store.rangePickerModal.open}
                        >{store.dateRange.label}</Button>
                    </div> : null}
                    {!single ? [this.props.legend1, this.props.legend2].map((legend, index) => <div
                        key={legend}
                        className={block('Legend', { index })}
                    >{legend}</div>) : null}
                </div>
                <div className={block('SVGContainer')} ref={this.svgContainerRel}>
                    <svg className={block('SVG')} ref={this.svgRel} />
                </div>
            </div>
        </div>;
    }

    render() {
        const { store } = this.props;
        return <div className={block()}>
            <div className={block('Head')}>
                {store.ranges.map(({ id, title }) => <Button
                    key={id}
                    className={block('Range', { active: id === store.rangeId })}
                    onClick={() => store.setRangeId(id)}
                >{title}</Button>)}
            </div>
            <div className={block('Body')}>
                {store.updateState.isLoading
                    ? <Loader className={block('Loader')} />
                    : null}
                {!store.updateState.isLoading && (store.updateState.successCount !== 0)
                    ? this.renderBody()
                    : null}
            </div>
            <RangePickerModal
                dateRangeStore={store.dateRange}
                isOpen={store.rangePickerModal.isOpen}
                onApply={store.applyCustomRange}
                onCancel={store.cancelCustomRange}
            />
        </div>;
    }
}

export default observer(WidgetLineChart);
